import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, ClipboardList } from 'lucide-react';
import DashboardLayout from '../components/Layout/DashboardLayout';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import {
  useAssignedOffboardingTasks,
  useCompleteOffboardingTask,
  useDecideLeaveOffset,
} from '../hooks/useOffboardingCases';
import { formatDate } from '../utils/dateUtils';
import { formatLeaveDays } from '../utils/numberUtils';

const OffboardingTasks = () => {
  const navigate = useNavigate();
  const { data: tasks = [], isLoading, isError, refetch } = useAssignedOffboardingTasks();
  const completeTask = useCompleteOffboardingTask();
  const decideOffset = useDecideLeaveOffset();

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-gray-900">Offboarding tasks</h1>
          <p className="text-sm text-gray-500">Sign off the clearance items assigned to you.</p>
        </div>

        {isLoading && <div className="flex justify-center py-12"><LoadingSpinner /></div>}

        {isError && (
          <div className="text-center py-12">
            <ClipboardList className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Could not load tasks</h3>
            <p className="text-sm text-gray-500">Try again in a moment.</p>
            <button type="button" className="btn btn-primary mt-4" onClick={() => refetch()}>
              Retry
            </button>
          </div>
        )}

        {!isLoading && !isError && tasks.length === 0 && (
          <div className="bg-white rounded-2xl border shadow-sm">
            <div className="text-center py-12 px-6">
              <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <ClipboardList className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No assigned tasks</h3>
              <p className="text-sm text-gray-500 max-w-sm mx-auto">
                You will see handover, payroll, IT or operations items here when HR assigns them.
              </p>
            </div>
          </div>
        )}

        <div className="space-y-3">
          {tasks.map((task) => (
            <div key={task.id} className="bg-white rounded-2xl border shadow-sm p-5">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900">{task.task_name}</p>
                  <p className="text-xs text-gray-500">
                    {task.employee_staff_id}
                    {task.due_date ? ` · due ${formatDate(task.due_date)}` : ''}
                    {task.playbook ? ` · ${task.playbook}` : ''}
                  </p>
                  {task.description && (
                    <p className="text-sm text-gray-700 mt-2">{task.description}</p>
                  )}
                  {task.task_key === 'leave_offset' && task.offset_request_id && (
                    <p className="text-sm text-gray-700 mt-2">
                      {formatLeaveDays(task.offset_days)} days AL · {formatDate(task.original_end_date)}
                      {' → '}
                      {formatDate(task.revised_end_date)}
                      {' · after '}
                      {task.revised_end_session === 'AM' ? 'morning' : 'afternoon'}
                      {' · '}
                      {formatLeaveDays(task.remaining_encash_days)} days remaining for encashment
                    </p>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {task.task_key === 'leave_offset' && task.offset_request_id ? (
                    <>
                      <button
                        type="button"
                        className="btn btn-outline btn-sm"
                        disabled={decideOffset.isPending}
                        onClick={() => decideOffset.mutate({ requestId: task.offset_request_id, data: { approved: false } })}
                      >
                        Reject
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        disabled={decideOffset.isPending}
                        onClick={() => decideOffset.mutate({ requestId: task.offset_request_id, data: { approved: true } })}
                      >
                        Approve offset
                      </button>
                    </>
                  ) : task.task_key === 'repatriation' ? (
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={() => navigate(`/admin/employee-lifecycle?tab=offboarding&case=${task.workflow_id}&section=operations`)}
                    >
                      Record departure
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      disabled={completeTask.isPending}
                      onClick={() => completeTask.mutate({ id: task.workflow_id, taskKey: task.task_key })}
                    >
                      <CheckCircle2 className="w-4 h-4" />
                      Sign off
                    </button>
                  )}
                  {task.section === 'finance' && (
                    <button
                      type="button"
                      className="btn btn-outline btn-sm"
                      onClick={() => navigate('/admin/payroll?tab=final-settlements')}
                    >
                      Open payroll
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default OffboardingTasks;
